import { useState, useEffect, useCallback } from 'react';
import { FaFileInvoiceDollar, FaDownload, FaTrash, FaTimes, FaEye, FaFilePdf, FaEnvelope } from 'react-icons/fa';
import api from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { useColumnVisibility, ColumnPicker } from '../../components/ColumnPicker';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

function getToken() {
  try { return localStorage.getItem('marvy_token'); } catch { return null; }
}

export default function AdminInvoices() {
  const { user } = useAuth();
  const isSuperAdmin = user?.permissions?.includes('generate_invoices');
  const [invoices, setInvoices] = useState([]);
  const [users, setUsers]       = useState([]);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [sendModal, setSendModal]           = useState(null); // invoice object
  const [sendTo, setSendTo]                 = useState('');
  const [sendBody, setSendBody]             = useState('');
  const [sending, setSending]               = useState(false);
  const [sendError, setSendError]           = useState('');
  const [sendHistory, setSendHistory]       = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [attachmentOptions, setAttachmentOptions]     = useState([]);
  const [attachmentsLoading, setAttachmentsLoading]   = useState(false);
  const [showAllTs, setShowAllTs]                     = useState(false);
  const [allTimesheets, setAllTimesheets]             = useState([]);
  const [allTsLoading, setAllTsLoading]               = useState(false);

  const defaultSendBody = (inv) => {
    if (!inv) return '';
    const monthYear = inv.period_end
      ? new Date(inv.period_end).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
      : '';
    return `Hi,\n\nThe Invoice and Timesheets are attached for ${monthYear}. Please let us know if you have any questions.\n\nBest regards,\nMARVY IT Services`;
  };

  // Generate form
  const [showGen, setShowGen]   = useState(false);
  const [genForm, setGenForm]   = useState({ userId: '', clientId: '', periodStart: '', periodEnd: '', invoiceDate: '' });
  const [preview, setPreview]   = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');

  const COLS = [
    { key: 'consultant', label: 'Consultant' },
    { key: 'period',     label: 'Period'      },
    { key: 'hours',      label: 'Hours'       },
    { key: 'due',        label: 'Due Date'    },
    { key: 'created',    label: 'Created'     },
  ];
  const { visible: visibleCols, toggle: toggleCol, show } = useColumnVisibility(COLS, 'cols_invoices');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.getInvoices(), api.getUsers(), api.getClients()])
      .then(([inv, u, c]) => { setInvoices(inv); setUsers(u); setClients(c); })
      .catch(() => setError('Failed to load data.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const setG = (k, v) => setGenForm(f => ({ ...f, [k]: v }));

  const handlePreview = async () => {
    setGenError(''); setPreview(null); setPreviewing(true);
    try {
      const data = await api.previewInvoice({
        clientId: parseInt(genForm.clientId),
        userId:   parseInt(genForm.userId),
        periodStart: genForm.periodStart,
        periodEnd:   genForm.periodEnd,
      });
      setPreview(data);
    } catch (err) {
      setGenError(err.message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleGenerate = async () => {
    setGenError(''); setGenerating(true);
    try {
      const token = getToken();
      const res = await fetch(`${BASE}/invoices/generate`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          clientId:    parseInt(genForm.clientId),
          userId:      parseInt(genForm.userId),
          periodStart: genForm.periodStart,
          periodEnd:   genForm.periodEnd,
          invoiceDate: genForm.invoiceDate || undefined,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to generate invoice');
      }
      const blob = await res.blob();
      // Auto-download
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const cd   = res.headers.get('content-disposition') || '';
      const fn   = cd.match(/filename="(.+?)"/)?.[1] || 'invoice.pdf';
      a.href = url; a.download = fn; a.click();
      URL.revokeObjectURL(url);

      setSuccess('Invoice generated and downloaded.');
      setShowGen(false);
      setGenForm({ userId: '', clientId: '', periodStart: '', periodEnd: '', invoiceDate: '' });
      setPreview(null);
      load();
    } catch (err) {
      setGenError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const openSendModal = async (inv) => {
    setSendModal(inv);
    setSendTo(inv.client_email || '');
    setSendBody(defaultSendBody(inv));
    setSendError('');
    setAttachmentOptions([]);
    setAllTimesheets([]);
    setShowAllTs(false);
    setSendHistory([]);
    setAttachmentsLoading(true);
    setHistoryLoading(true);
    try {
      const [atts, history] = await Promise.all([
        api.getInvoiceAttachments(inv.id),
        api.getInvoiceSends(inv.id),
      ]);
      setAttachmentOptions(atts.map(a => ({ ...a, checked: true })));
      setSendHistory(history);
    } catch { /* non-critical */ }
    finally { setAttachmentsLoading(false); setHistoryLoading(false); }
  };

  const handleToggleAllTs = async () => {
    if (showAllTs) { setShowAllTs(false); return; }
    setShowAllTs(true);
    if (allTimesheets.length > 0) return; // already loaded
    setAllTsLoading(true);
    try {
      const all = await api.getInvoiceEmployeeTimesheets(sendModal.id);
      setAllTimesheets(all);
    } catch { /* non-critical */ }
    finally { setAllTsLoading(false); }
  };

  const toggleAllTs = (ts) => {
    const alreadyIn = attachmentOptions.some(a => a.id === ts.id);
    if (alreadyIn) {
      setAttachmentOptions(prev => prev.filter(a => a.id !== ts.id));
    } else {
      setAttachmentOptions(prev => [...prev, { ...ts, checked: true }]);
    }
  };

  const handleSend = async () => {
    if (!sendModal) return;
    setSending(true); setSendError('');
    try {
      const payload = {
        messageBody: sendBody,
        selectedIds: attachmentOptions.filter(a => a.checked).map(a => a.id),
      };
      if (sendTo.trim()) payload.to = sendTo.trim();
      const result = await api.sendInvoice(sendModal.id, payload);
      setSuccess(result.message || 'Invoice sent.');
      setSendModal(null);
      setSendTo('');
      setSendBody('');
      load(); // refresh send_count badge
    } catch (err) {
      setSendError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleDownload = (id) => {
    const token = getToken();
    // Use direct URL with token query param — works on mobile/iOS Safari
    window.open(`${BASE}/invoices/${id}/pdf?token=${token}`, '_blank');
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteInvoice(id);
      setSuccess('Invoice deleted.');
      setConfirmDelete(null);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', timeZone: 'UTC' }) : '—';
  const fmtDateRange = (start, end) => {
    if (!start) return '—';
    const s = new Date(start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    const e = end ? new Date(end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '';
    return e ? `${s} – ${e}` : s;
  };
  const fmtMoney = (v) => `$${Number(v || 0).toFixed(2)}`;

  const activeEmployees = users.filter(u => u.active !== false);
  const activeClients   = clients.filter(c => c.is_active !== false);
  const canPreview = genForm.userId && genForm.clientId && genForm.periodStart && genForm.periodEnd;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0c1b4d] flex items-center justify-center">
              <FaFileInvoiceDollar className="text-[#D4AF37]" size={16} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#0c1b4d]">Invoices</h1>
              <p className="text-xs text-gray-500">{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          {isSuperAdmin && (
            <button onClick={() => { setShowGen(true); setPreview(null); setGenError(''); setGenForm({ userId: '', clientId: '', periodStart: '', periodEnd: '', invoiceDate: '' }); }}
              className="flex items-center gap-2 px-4 py-2 bg-[#0c1b4d] text-white rounded-lg text-sm font-semibold hover:bg-blue-900 transition-colors">
              <FaFilePdf size={12} /> Generate Invoice
            </button>
          )}
        </div>

        {error   && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
        {success && <div className="mb-4 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{success}</div>}

        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading…</div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-16 text-gray-400">No invoices yet. Generate one above.</div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <ColumnPicker columns={COLS} visible={visibleCols} onToggle={toggleCol} />

            {/* Scrollable table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Invoice #</th>
                    {(show('consultant')) && <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Consultant</th>}
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Client</th>
                    {show('period')  && <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Period</th>}
                    {show('hours')   && <th className="px-4 py-3 text-right font-semibold text-gray-600 whitespace-nowrap">Hours</th>}
                    <th className="px-4 py-3 text-right font-semibold text-gray-600 whitespace-nowrap">Total</th>
                    {show('due')     && <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Due</th>}
                    {show('created') && <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Created</th>}
                    <th className="px-4 py-3 text-right font-semibold text-gray-600 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {invoices.map(inv => (
                    <tr key={inv.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-mono font-semibold text-[#0c1b4d] text-xs">{inv.invoice_number}</span>
                        {inv.send_count > 0 && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded-full font-medium">
                              <FaEnvelope size={9} /> Sent {inv.send_count}×
                            </span>
                            <span className="text-xs text-gray-400">{new Date(inv.last_sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          </div>
                        )}
                      </td>
                      {show('consultant') && <td className="px-4 py-3 text-gray-800 whitespace-nowrap">{inv.consultant_name}</td>}
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{inv.client_name}</td>
                      {show('period')  && <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}</td>}
                      {show('hours')   && <td className="px-4 py-3 text-right text-gray-700">{Number(inv.hours).toFixed(1)}</td>}
                      <td className="px-4 py-3 text-right font-semibold text-gray-800">{fmtMoney(inv.final_total)}</td>
                      {show('due')     && <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDate(inv.due_date)}</td>}
                      {show('created') && <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—'}</td>}
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => handleDownload(inv.id)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Download PDF">
                            <FaDownload size={13} />
                          </button>
                          <button onClick={() => openSendModal(inv)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Send to client">
                            <FaEnvelope size={13} />
                          </button>
                          <button onClick={() => setConfirmDelete(inv)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" title="Delete">
                            <FaTrash size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Generate Invoice Modal */}
      {showGen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-base font-bold text-[#0c1b4d]">Generate Invoice</h2>
              <button onClick={() => setShowGen(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><FaTimes size={14} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {genError && <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{genError}</div>}

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Consultant *</label>
                <select value={genForm.userId} onChange={e => { setG('userId', e.target.value); setPreview(null); }} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]">
                  <option value="">Select employee…</option>
                  {activeEmployees.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Client *</label>
                <select value={genForm.clientId} onChange={e => { setG('clientId', e.target.value); setPreview(null); }} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]">
                  <option value="">Select client…</option>
                  {activeClients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Period Start *</label>
                  <input type="date" value={genForm.periodStart} onChange={e => { setG('periodStart', e.target.value); setPreview(null); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Period End *</label>
                  <input type="date" value={genForm.periodEnd} onChange={e => { setG('periodEnd', e.target.value); setPreview(null); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Invoice Date <span className="font-normal text-gray-400">(defaults to today)</span></label>
                <input type="date" value={genForm.invoiceDate} onChange={e => setG('invoiceDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]" />
              </div>

              {/* Preview button */}
              <button onClick={handlePreview} disabled={!canPreview || previewing}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-[#0c1b4d] text-[#0c1b4d] rounded-lg text-sm font-semibold hover:bg-blue-50 transition-colors disabled:opacity-50">
                <FaEye size={13} /> {previewing ? 'Calculating…' : 'Preview Invoice'}
              </button>

              {/* Preview card */}
              {preview && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Consultant</span>
                    <span className="font-medium">{preview.consultantName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Client</span>
                    <span className="font-medium">{preview.clientName}</span>
                  </div>
                  {preview.project && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Project</span>
                      <span className="font-medium">{preview.project}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Approved Hours</span>
                    <span className="font-semibold">{Number(preview.hours).toFixed(1)} hrs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Rate</span>
                    <span>${Number(preview.rate).toFixed(2)}/hr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Subtotal</span>
                    <span>{fmtMoney(preview.total)}</span>
                  </div>
                  {preview.earlyPayment && preview.discountPerHour > 0 && (
                    <div className="flex justify-between text-green-700">
                      <span>Early Payment Discount</span>
                      <span>-{fmtMoney(preview.discountedTotal)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-gray-300 pt-2 mt-1">
                    <span className="font-bold text-[#0c1b4d]">Total Due</span>
                    <span className="font-bold text-[#0c1b4d] text-base">{fmtMoney(preview.finalTotal)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>Due Date</span>
                    <span>{fmtDate(preview.dueDate)}</span>
                  </div>
                  {preview.hours === 0 && (
                    <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-xs">
                      No approved timesheets found for this period. The invoice will show 0 hours.
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <button onClick={() => setShowGen(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
                  Cancel
                </button>
                <button onClick={handleGenerate} disabled={!preview || generating}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0c1b4d] text-white rounded-lg text-sm font-semibold hover:bg-blue-900 transition-colors disabled:opacity-50">
                  <FaFilePdf size={13} /> {generating ? 'Generating…' : 'Generate & Download'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Invoice Modal */}
      {sendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4 py-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm sm:max-w-lg md:max-w-2xl max-h-[90vh] overflow-y-auto px-6 py-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-[#0c1b4d]">Send Invoice</h3>
              <button onClick={() => setSendModal(null)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><FaTimes size={14} /></button>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Sending <span className="font-semibold text-gray-800">{sendModal.invoice_number}</span> to <span className="font-semibold text-gray-800">{sendModal.client_name}</span>.
            </p>
            <p className="text-xs font-semibold text-gray-600 mb-2">Attachments</p>
            {/* Attachments grid */}
            <div className="mb-4 border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="w-8 px-3 py-2"></th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">Date Range</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">File</th>
                    <th className="w-8 px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {attachmentsLoading ? (
                    <tr><td colSpan={4} className="px-3 py-4 text-gray-400 text-center">Loading attachments…</td></tr>
                  ) : attachmentOptions.length === 0 ? (
                    <tr><td colSpan={4} className="px-3 py-4 text-gray-400 text-center">No attachments found.</td></tr>
                  ) : (
                    attachmentOptions.map(att => (
                      <tr key={att.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2.5 text-center">
                          <input type="checkbox" checked={att.checked}
                            onChange={e => setAttachmentOptions(prev =>
                              prev.map(a => a.id === att.id ? { ...a, checked: e.target.checked } : a)
                            )}
                            className="w-4 h-4 accent-[#0c1b4d]" />
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-gray-600">
                          {att.type === 'invoice' ? (
                            <span className="font-medium text-[#0c1b4d]">Invoice PDF</span>
                          ) : (
                            fmtDateRange(att.weekStart, att.weekEnd)
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-gray-700 max-w-[180px]">
                          <span className="truncate block" title={att.filename}>{att.filename}</span>
                        </td>
                        <td className="px-2 py-2.5 text-center">
                          {att.timesheetId && (
                            <a href={`${BASE}/timesheets/${att.timesheetId}/attachment?token=${getToken()}`}
                              target="_blank" rel="noreferrer"
                              className="p-1 text-blue-500 hover:text-blue-700 inline-block" title="View file">
                              <FaDownload size={11} />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Browse all employee timesheets */}
            <button type="button" onClick={handleToggleAllTs}
              className="w-full mb-3 flex items-center justify-between px-3 py-2 border border-dashed border-gray-300 rounded-xl text-xs font-medium text-gray-500 hover:border-[#0c1b4d] hover:text-[#0c1b4d] transition-colors">
              <span>{showAllTs ? 'Hide all timesheets' : 'Browse all employee timesheets…'}</span>
              <span>{showAllTs ? '▲' : '▼'}</span>
            </button>
            {showAllTs && (
              <div className="mb-4 border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-gray-600">All timesheets with attachments</p>
                    <p className="text-xs text-gray-400">Check to add to this email</p>
                  </div>
                </div>
                <div className="max-h-48 sm:max-h-64 md:max-h-80 overflow-y-auto">
                  {allTsLoading ? (
                    <p className="text-xs text-gray-400 px-3 py-4 text-center">Loading…</p>
                  ) : allTimesheets.length === 0 ? (
                    <p className="text-xs text-gray-400 px-3 py-4 text-center">No timesheets with attachments found.</p>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                        <tr>
                          <th className="w-8 px-3 py-2"></th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-500">Date Range</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-500">File</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-500">Status</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-500">Hrs</th>
                          <th className="w-8 px-2 py-2"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {allTimesheets.map(ts => {
                          const isAdded = attachmentOptions.some(a => a.id === ts.id);
                          return (
                            <tr key={ts.id} className={`hover:bg-blue-50 transition-colors ${isAdded ? 'bg-blue-50' : ''}`}>
                              <td className="px-3 py-2.5 text-center">
                                <input type="checkbox" checked={isAdded}
                                  onChange={() => toggleAllTs(ts)}
                                  className="w-4 h-4 accent-[#0c1b4d]" />
                              </td>
                              <td className="px-3 py-2.5 whitespace-nowrap text-gray-600 font-medium">
                                {fmtDateRange(ts.weekStart, ts.weekEnd)}
                              </td>
                              <td className="px-3 py-2.5 text-gray-700 max-w-[160px]">
                                <span className="truncate block" title={ts.filename}>{ts.filename}</span>
                              </td>
                              <td className="px-3 py-2.5">
                                <span className={`px-1.5 py-0.5 rounded-full font-medium ${
                                  ts.status === 'approved'  ? 'bg-emerald-50 text-emerald-700' :
                                  ts.status === 'submitted' ? 'bg-amber-50 text-amber-700' :
                                  'bg-gray-100 text-gray-500'}`}>
                                  {ts.status}
                                </span>
                              </td>
                              <td className="px-3 py-2.5 text-right text-gray-500">{ts.hours}</td>
                              <td className="px-2 py-2.5 text-center">
                                <a href={`${BASE}/timesheets/${ts.timesheetId}/attachment?token=${getToken()}`}
                                  target="_blank" rel="noreferrer"
                                  className="p-1 text-blue-500 hover:text-blue-700 inline-block" title="View file">
                                  <FaDownload size={11} />
                                </a>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
            {/* Send history */}
            {(historyLoading || sendHistory.length > 0) && (
              <div className="mb-4 bg-gray-50 border border-gray-200 rounded-xl p-3">
                <p className="text-xs font-semibold text-gray-500 mb-2">Send History</p>
                {historyLoading ? (
                  <p className="text-xs text-gray-400">Loading…</p>
                ) : (
                  <div className="space-y-1.5">
                    {sendHistory.map(h => (
                      <div key={h.id} className="flex items-center justify-between gap-2">
                        <span className="text-xs text-gray-700 truncate">{h.sent_to}</span>
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {new Date(h.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {h.sent_by_name && <span className="ml-1">· {h.sent_by_name}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {sendError && <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{sendError}</div>}
            <label className="block text-xs font-semibold text-gray-600 mb-1">Recipient email</label>
            <input
              type="email"
              value={sendTo}
              onChange={e => setSendTo(e.target.value)}
              placeholder={sendModal.client_email || 'Enter email address…'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d] mb-1"
            />
            <p className="text-xs text-gray-400 mb-4">Leave blank to use the client's email on file.</p>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Email message</label>
            <textarea
              rows={6}
              value={sendBody}
              onChange={e => setSendBody(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d] resize-none mb-4"
            />
            <div className="flex gap-3">
              <button onClick={() => setSendModal(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">Cancel</button>
              <button onClick={handleSend} disabled={sending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#0c1b4d] text-white rounded-lg text-sm font-semibold hover:bg-blue-900 transition-colors disabled:opacity-50">
                <FaEnvelope size={12} /> {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm px-6 py-5">
            <h3 className="text-base font-bold text-gray-800 mb-2">Delete Invoice?</h3>
            <p className="text-sm text-gray-600 mb-5">
              Permanently delete <strong>{confirmDelete.invoice_number}</strong>? The PDF will be lost.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleDelete(confirmDelete.id)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
